import styled from "styled-components";
import { IPaymentReceiver } from "src/Interfaces/paymentReceiver";
import { StyledButton } from "@components";
import { buttonGradient, theme } from "@utilities";
import { useState } from "react";

interface ConfirmPaymentLinkModalProps {
    traveler: IPaymentReceiver;
    onConfirm: () => Promise<void>;
    onCancel: () => void;
    onCloseParent: () => void;
}

export const ConfirmPaymentLinkModal = ({
    traveler,
    onConfirm,
    onCancel,
    onCloseParent,
}: ConfirmPaymentLinkModalProps) => {
    const [isLoading, setIsLoading] = useState(false);

    const handleConfirm = async () => {
        setIsLoading(true);
        try {
            await onConfirm();
            onCancel();
            onCloseParent();
        } finally {
            setIsLoading(false);
        }
    };

    const handleCancel = () => {
        onCancel();
        onCloseParent();
    };

    return (
        <>
            <Overlay onClick={handleCancel} />
            <ConfirmModal>
                <CloseIcon onClick={handleCancel}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path
                            d="M18 6L6 18M6 6L18 18"
                            stroke="#080914"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                </CloseIcon>
                <ConfirmContent>
                    <TextContainer>
                        <Title>Confirm email payment link</Title>
                        <TravelerInfo>
                            <TravelerDetails>
                                <TravelerName>{traveler.name}</TravelerName>
                                <TravelerEmail>{traveler.email}</TravelerEmail>
                            </TravelerDetails>
                        </TravelerInfo>
                    </TextContainer>
                    <ButtonContainer>
                        <ConfirmButton onClick={handleConfirm} disabled={isLoading}>
                            <ConfirmText>{isLoading ? "Sending..." : "Confirm"}</ConfirmText>
                        </ConfirmButton>
                        <CancelButton onClick={handleCancel}>
                            <CancelText>Cancel</CancelText>
                        </CancelButton>
                    </ButtonContainer>
                </ConfirmContent>
            </ConfirmModal>
        </>
    );
};

const Overlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 20;
`;

const ConfirmModal = styled.div`
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 430px;
    height: 336px;
    background: #ffffff;
    border-radius: 8px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: flex-end;
    padding: 10px 24px 24px;
    gap: 16px;
    z-index: 25;
    box-sizing: border-box;
`;

const ConfirmContent = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 0;
    gap: 24px;
    width: 382px;
    height: 256px;
    flex: none;
    order: 1;
    flex-grow: 0;
`;

const CloseIcon = styled.button`
    background: none;
    border: none;
    width: 24px;
    height: 24px;
    cursor: pointer;
    flex: none;
    order: 0;
    flex-grow: 0;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    position: absolute;
    top: 10px;
    right: 24px;
`;

const TextContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    padding: 0;
    gap: 24px;
    width: 279px;
    height: 106px;
    flex: none;
    order: 0;
    flex-grow: 0;
    margin-top: 18px;
`;

const Title = styled.div`
    width: 279px;
    height: 22px;
    font-family: "Poppins";
    font-style: normal;
    font-weight: 600;
    font-size: 18px;
    line-height: 120%;
    color: #131214;
    flex: none;
    order: 0;
    flex-grow: 0;
`;

const TravelerInfo = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    padding: 4px 8px;
    gap: 10px;
    width: 279px;
    height: 60px;
    background: #f4f6f7;
    flex: none;
    order: 1;
    align-self: stretch;
    flex-grow: 0;
`;

const TravelerDetails = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    padding: 0;
    gap: 8px;
    width: 259px;
    height: 42px;
    flex: none;
    order: 0;
    flex-grow: 0;
`;

const TravelerName = styled.div`
    width: 259px;
    height: 22px;
    font-family: "Poppins";
    font-style: normal;
    font-weight: 600;
    font-size: 18px;
    line-height: 120%;
    display: flex;
    align-items: center;
    color: #131214;
    flex: none;
    order: 0;
    align-self: stretch;
    flex-grow: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const TravelerEmail = styled.div`
    width: 259px;
    height: 12px;
    font-family: "Plus Jakarta Sans";
    font-style: normal;
    font-weight: 400;
    font-size: 12px;
    line-height: 120%;
    display: flex;
    align-items: center;
    color: #6e7375;
    flex: none;
    order: 1;
    align-self: stretch;
    flex-grow: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const ButtonContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    padding: 0;
    gap: 18px;
    width: 278px;
    height: 126px;
    flex: none;
    order: 1;
    flex-grow: 0;
`;

const ConfirmButton = styled(StyledButton)`
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    padding: 17px 24px;
    gap: 10px;
    width: 278px;
    height: 54px;
    background: ${buttonGradient(theme.gradients.button)};
    border-radius: 4px;
    flex: none;
    order: 0;
    align-self: stretch;
    flex-grow: 0;
    margin-top: 0 !important;
    cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
    opacity: ${({ disabled }) => (disabled ? 0.6 : 1)};
`;

const ConfirmText = styled.div`
    margin: 0;
    width: 63px;
    height: 26px;
    font-family: "Poppins";
    font-style: normal;
    font-weight: 700;
    font-size: 16px;
    line-height: 26px;
    display: flex;
    align-items: center;
    text-align: center;
    color: ${theme.colors.button_text};
    flex: none;
    order: 0;
    flex-grow: 0;
`;

const CancelButton = styled(StyledButton)`
    box-sizing: border-box;
    width: 278px;
    height: 54px;
    border-radius: 4px;
    flex: none;
    order: 1;
    align-self: stretch;
    flex-grow: 0;
    position: relative;
    background: transparent;
    border: 1px solid ${theme.colors.main};
    margin-top: 0 !important;
`;

const CancelText = styled.div`
    position: absolute;
    width: 90px;
    height: 20px;
    left: calc(55% - 90px / 2 - 0.5px);
    top: 16.5px;
    font-family: "Poppins";
    font-style: normal;
    font-weight: 700;
    font-size: 16px;
    line-height: 26px;
    display: flex;
    align-items: center;
    text-align: center;
    color: ${theme.colors.button_text1};
`;
